import React from 'react';
import './ConfirmDialog.css';

/**
 * Custom confirmation dialog component
 * @param {boolean} isOpen - Whether dialog is visible
 * @param {function} onConfirm - Callback when user confirms
 * @param {function} onCancel - Callback when user cancels
 * @param {string} title - Dialog title
 * @param {string} message - Dialog message (can contain newlines)
 * @param {string} confirmText - Text for confirm button
 * @param {string} cancelText - Text for cancel button
 * @param {string} variant - 'warning' | 'critical' | 'info'
 */
const ConfirmDialog = ({
    isOpen,
    onConfirm,
    onCancel,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'warning'
}) => {
    // Split message by newlines for better rendering
    const messageLines = message ? message.split('\n').filter(line => line.trim()) : [];

    const handleConfirm = () => {
        onConfirm();
    };

    const handleCancel = () => {
        onCancel();
    };

    // Close on backdrop click
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            handleCancel();
        }
    };

    // Close on Escape key - Hook must be called before any early returns
    React.useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                handleCancel();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen]);

    // Early return after all hooks
    if (!isOpen) return null;

    // Icon mapping for different variants
    const variantIcons = {
        warning: '⚠️',
        critical: '🛑',
        info: 'ℹ️'
    };

    return (
        <div className="confirm-dialog-backdrop" onClick={handleBackdropClick}>
            <div className={`confirm-dialog confirm-dialog-${variant}`}>
                <div className="confirm-dialog-header">
                    <div className="confirm-dialog-icon">
                        {variantIcons[variant] || variantIcons.info}
                    </div>
                    <h3 className="confirm-dialog-title">{title}</h3>
                </div>

                <div className="confirm-dialog-body">
                    {messageLines.map((line, index) => (
                        <p key={index} className="confirm-dialog-line">
                            {line}
                        </p>
                    ))}
                </div>

                <div className="confirm-dialog-footer">
                    <button
                        className="confirm-dialog-button confirm-dialog-button-cancel"
                        onClick={handleCancel}
                    >
                        {cancelText}
                    </button>
                    <button
                        className={`confirm-dialog-button confirm-dialog-button-confirm confirm-dialog-button-${variant}`}
                        onClick={handleConfirm}
                        autoFocus
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmDialog;
